import { Controller } from "@hotwired/stimulus"
import posthog from "posthog-js"

export default class extends Controller {
  static targets = ["banner", "acceptButton", "declineButton"]
  static classes = ["hidden"]

  declare readonly bannerTarget: HTMLElement
  declare readonly acceptButtonTarget: HTMLElement
  declare readonly declineButtonTarget: HTMLElement
  declare readonly hiddenClasses: string[]

  private boundHideBeforeCache: (() => void) | null = null

  connect() {
    // Connection marker for system tests, set before any early return:
    // absence assertions wait on it so they can't pass before this
    // controller has actually run (Turbo is defined before Stimulus
    // scans the DOM).
    this.element.setAttribute("data-consent-connected", "")

    // Never auto-show during automated tests — the banner overlays the page
    // bottom and intercepts clicks near it (#1343). Mirrors the test-mode
    // guard in the Inertia ConsentManager component.
    if (import.meta.env.MODE === "test") return

    this.checkConsent()
    // Hide banner before Turbo caches the page to prevent overlay issues
    // Store bound function reference to ensure proper cleanup in disconnect()
    this.boundHideBeforeCache = this.hideBeforeCache.bind(this)
    document.addEventListener("turbo:before-cache", this.boundHideBeforeCache)
  }

  disconnect() {
    // Remove the exact listener reference that was added
    if (this.boundHideBeforeCache) {
      document.removeEventListener(
        "turbo:before-cache",
        this.boundHideBeforeCache,
      )
      this.boundHideBeforeCache = null
    }
  }

  hideBeforeCache() {
    // Always hide the banner before caching
    this.hiddenClasses.forEach((cls) => this.bannerTarget.classList.add(cls))
  }

  checkConsent() {
    try {
      const consent = localStorage.getItem("posthog_consent")
      if (consent === null) {
        this.hiddenClasses.forEach((cls) =>
          this.bannerTarget.classList.remove(cls),
        )
      } else {
        this.hiddenClasses.forEach((cls) =>
          this.bannerTarget.classList.add(cls),
        )
      }
    } catch {
      // If localStorage is not available, show banner
      this.hiddenClasses.forEach((cls) =>
        this.bannerTarget.classList.remove(cls),
      )
    }
  }

  // The SDK call sits OUTSIDE the try/catch, matching consent-manager.tsx.
  // Inside it, a localStorage throw (Safari private mode, partitioned
  // iframe) skipped the opt-in entirely while reporting only "Unable to
  // save consent" — survivable when the call was a no-op against the
  // unassigned `window.posthog`, but it is now the thing that actually
  // turns tracking on or off.
  handleAccept() {
    try {
      localStorage.setItem("posthog_consent", "true")
      this.hiddenClasses.forEach((cls) => this.bannerTarget.classList.add(cls))
    } catch (e) {
      console.warn("Unable to save consent:", e)
    }
    posthog.opt_in_capturing()
  }

  handleDecline() {
    try {
      localStorage.setItem("posthog_consent", "false")
      this.hiddenClasses.forEach((cls) => this.bannerTarget.classList.add(cls))
    } catch (e) {
      console.warn("Unable to save consent:", e)
    }
    posthog.opt_out_capturing()
  }
}
